<!--
  TwoFactor.svelte — Two-factor authentication verification page.

  Shown after a login attempt returns `requiresTwoFactor: true`.
  The temporary token is passed via the URL query string
  (`#/two-factor?twoFactorToken=...`).

  The user enters their 6-digit TOTP code (from an authenticator app)
  or a backup code.  On success the real JWT is returned and the
  user is redirected to the dashboard.
-->
<script lang="ts">
	import { run, preventDefault } from 'svelte/legacy';

	import { onMount } from 'svelte';
	import { Button } from 'flowbite-svelte';
	import { push, querystring } from '../../abstract-spa-router';
	import AuthLayout from './AuthLayout.svelte';
	import { FormField, AlertMessage, OTPInput, LoadingOverlay, RoutedLink } from '../../components';
	import AppBrandingCard from '../../components/AppBrandingCard.svelte';
	import { setToken, refreshTokenPeriodically } from '../../Auth';
	import { verifyTwoFactor, getApplicationPublicInfo, savePreferredAuthMethod } from '../../AppNetwork';
	import type { ApplicationPublicInfo } from '../../AppTypes';
	import { t, t2 } from '../../i18n';

	// ── URL params ────────────────────────────────────────────

	let params = $derived(new URLSearchParams($querystring || ''));
	let twoFactorToken = $derived(params.get('twoFactorToken') || '');

	// ── State ─────────────────────────────────────────────────

	/** Whether the user is entering a backup code instead of TOTP. */
	let useBackupCode = $state(false);
	/** Backup code value (free-text input). */
	let backupCode = $state('');
	/** Whether a network request is in progress. */
	let loading = $state(false);
	/** Top-level error message. */
	let errorMessage = $state('');

	/** Public info about the requesting application (for branded UI). */
	let appInfo: ApplicationPublicInfo | null = $state(null);

	/** URL to redirect to after 2FA (the original /authorize?... URL). */
	let redirectAfterLogin = '';

	/**
	 * Read the app context from the URL query string and fetch the public
	 * app info for branded UI. All state is in the URL — no sessionStorage.
	 *
	 * Two URL shapes are supported (mirroring Login.svelte):
	 *  1. Top-level params (`?app=…&returnURL=…` or with OAuth params) —
	 *     the post-2FA destination is rebuilt as `/authorize?…`.
	 *  2. Legacy `redirect=` (invitation flow) — used verbatim.
	 */
	onMount(async () => {
		const p = new URLSearchParams($querystring || '');
		const explicitRedirect = p.get('redirect');
		const appParam = p.get('app') || '';

		/* Resolve the post-2FA destination using the same precedence as
		   Login.svelte: explicit redirect wins, otherwise rebuild the
		   /authorize URL from the carried-through top-level params. */
		if (explicitRedirect) {
			redirectAfterLogin = explicitRedirect;
		} else if (appParam) {
			const authorizeParams = new URLSearchParams(p);
			authorizeParams.delete('twoFactorToken');
			/* Drop the remember-preference params — they are 2FA-only. */
			authorizeParams.delete('authMethod');
			authorizeParams.delete('remember');
			redirectAfterLogin = `/authorize?${authorizeParams.toString()}`;
		} else {
			redirectAfterLogin = '';
		}

		/** Fetch public app info for branded 2FA UI when an app param is present. */
		if (appParam) {
			try {
				appInfo = await getApplicationPublicInfo(appParam);
			} catch {
				/* Non-critical — 2FA still works, just without branding. */
			}
		}
	});

	// ── Guard ─────────────────────────────────────────────────

	/** Redirect to login if no two-factor token is present. */
	run(() => {
		if (!twoFactorToken) {
			push('/login');
		}
	});

	// ── Handlers ──────────────────────────────────────────────

	/** Returns true if an external app authorization flow is active. */
	function hasAppContext(): boolean {
		return !!redirectAfterLogin;
	}

	/**
	 * Validate the `authMethod` URL param into a rememberable method.
	 *
	 * @param value - Raw `authMethod` query param.
	 * @returns The method, or `undefined` if absent/unrecognised.
	 */
	function rememberableMethod(value: string | null): 'email' | 'password' | 'passkey' | undefined {
		/* Only the three chooser methods are persisted. */
		if (value === 'email' || value === 'password' || value === 'passkey') return value;
		return undefined;
	}

	/**
	 * Complete login after successful 2FA verification.
	 *
	 * Persists the remembered sign-in method carried from the login page
	 * (the login page never reaches its own `completeLogin` when 2FA is
	 * required), then redirects to the authorize page (with full params)
	 * if an app context is present, otherwise to the admin dashboard.
	 */
	async function completeLogin(token: string): Promise<void> {
		setToken(token);
		refreshTokenPeriodically();

		/* Save before navigating — the admin redirect is a full page load
		   that would otherwise cancel the in-flight account update. */
		await savePreferredAuthMethod(rememberableMethod(params.get('authMethod')), params.get('remember') === '1');

		/** Redirect to the authorize page if an app context is active, otherwise to admin. */
		if (hasAppContext()) {
			push(redirectAfterLogin);
		} else {
			/* Cross-app navigation to the admin app. */
			window.location.href = '/Manage/';
		}
	}

	/**
	 * Handle TOTP code completion from the OTP input.
	 */
	async function handleOTPComplete(code: string): Promise<void> {
		await submitCode(code);
	}

	/**
	 * Handle backup code submission.
	 */
	async function handleBackupSubmit(): Promise<void> {
		if (!backupCode.trim()) {
			errorMessage = t2('Please enter a backup code.');
			return;
		}
		await submitCode(backupCode.trim());
	}

	/**
	 * Submit the verification code (TOTP or backup) to the server.
	 */
	async function submitCode(code: string): Promise<void> {
		loading = true;
		errorMessage = '';
		/** Verify the TOTP/backup code and complete login on success. */
		try {
			const result = await verifyTwoFactor(twoFactorToken, code);
			await completeLogin(result.token);
		} catch (err: any) {
			errorMessage = err?.message || t2('Invalid code. Please try again.');
		} finally {
			loading = false;
		}
	}

	/** Handle Enter key on backup code input. */
	function handleKeypress(event: KeyboardEvent): void {
		if (event.key === 'Enter') handleBackupSubmit();
	}
</script>

<AuthLayout
	heading={appInfo ? '' : t2('Two-factor authentication')}
	subheading={appInfo ? '' : t2('Enter the code from your authenticator app.')}
>
	{#if appInfo}
		<AppBrandingCard
			applicationName={appInfo.applicationName}
			description={appInfo.description}
			logoImageName={appInfo.logoImageName}
			iconImageName={appInfo.iconImageName}
			icon={appInfo.icon}
		/>
	{/if}

	{#if loading}
		<LoadingOverlay />
	{/if}

	{#if errorMessage}
		<AlertMessage type="error" message={errorMessage} onclose={() => (errorMessage = '')} />
	{/if}

	{#if !useBackupCode}
		<!-- TOTP code input -->
		<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
			{$t("Open your authenticator app and enter the 6-digit code.")}
		</p>

		<OTPInput oncomplete={handleOTPComplete} error={errorMessage ? ' ' : ''} />
	{:else}
		<!-- Backup code input -->
		<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
			{$t("Enter one of your backup codes. Each code can only be used once.")}
		</p>

		<form onsubmit={preventDefault(handleBackupSubmit)}>
			<FormField
				id="backup-code"
				label={$t("Backup code")}
				placeholder={$t("e.g. a1b2c3d4")}
				bind:value={backupCode}
				required
				onkeypress={handleKeypress}
			/>

			<Button type="submit" class="w-full" disabled={loading}>{$t("Verify")}</Button>
		</form>
	{/if}

	<!-- Toggle between TOTP and backup code -->
	<div class="mt-6 text-center">
		<button
			type="button"
			class="text-sm text-primary-600 hover:underline dark:text-primary-400"
			onclick={() => {
				useBackupCode = !useBackupCode;
				errorMessage = '';
			}}
		>
			{useBackupCode ? $t('Use authenticator app instead') : $t('Use a backup code instead')}
		</button>
	</div>

	<!-- Back to login -->
	<div class="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
		<RoutedLink to="/login">
			<span class="font-medium text-primary-600 hover:underline dark:text-primary-400">{$t("Back to sign in")}</span>
		</RoutedLink>
	</div>
</AuthLayout>
